// src/services/NotificationService.ts
/**
 * 🔔 Notification Service
 * Manages persistent user notifications
 */

import { supabase } from '../config/supabase';
import { SupabaseAuthService } from './SupabaseAuthService';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface UserNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  read_at: string | null;
  action_url: string | null;
  metadata: Record<string, any>;
}

export class NotificationService {
  /**
   * Create a new notification for a user
   */
  static async createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    actionUrl?: string,
    metadata?: Record<string, any>
  ): Promise<{ success: boolean; notificationId?: string; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('create_notification', {
        p_user_id: userId,
        p_type: type,
        p_title: title,
        p_message: message,
        p_action_url: actionUrl || null,
        p_metadata: metadata || {}
      });

      if (error) {
        console.error('Error creating notification:', error);
        return { success: false, error: error.message };
      }

      return { success: true, notificationId: data };
    } catch (error: any) {
      console.error('Error in createNotification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get notifications for current user
   */
  static async getUserNotifications(
    limit: number = 50,
    onlyUnread: boolean = false
  ): Promise<UserNotification[]> {
    try {
      const user = await SupabaseAuthService.getCurrentUser();
      if (!user) return [];

      const { data, error } = await supabase.rpc('get_user_notifications', {
        p_user_id: user.id,
        p_limit: limit,
        p_only_unread: onlyUnread
      });

      if (error) {
        console.error('Error getting notifications:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserNotifications:', error);
      return [];
    }
  }

  /**
   * Mark a notification as read
   */
  static async markAsRead(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.rpc('mark_notification_as_read', {
        p_notification_id: notificationId
      });

      if (error) {
        console.error('Error marking notification as read:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error in markAsRead:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark all notifications as read for current user
   */
  static async markAllAsRead(): Promise<{ success: boolean; error?: string }> {
    try {
      const user = await SupabaseAuthService.getCurrentUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      const { error } = await supabase.rpc('mark_all_notifications_as_read', {
        p_user_id: user.id
      });

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error in markAllAsRead:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get count of unread notifications
   */
  static async getUnreadCount(): Promise<number> {
    try {
      const user = await SupabaseAuthService.getCurrentUser();
      if (!user) return 0;

      const { data, error } = await supabase.rpc('get_unread_notification_count', {
        p_user_id: user.id
      });

      if (error) {
        console.error('Error getting unread count:', error);
        return 0;
      }

      return data || 0;
    } catch (error) {
      console.error('Error in getUnreadCount:', error);
      return 0;
    }
  }

  /**
   * Delete a notification
   */
  static async deleteNotification(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.rpc('delete_notification', {
        p_notification_id: notificationId
      });

      if (error) {
        console.error('Error deleting notification:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error in deleteNotification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Helper: Create notification for file upload success
   */
  static async notifyFileUploadSuccess(userId: string, fileName: string, tradeCount: number): Promise<void> {
    await this.createNotification(
      userId,
      'success',
      'File Caricato con Successo',
      `Il file "${fileName}" è stato caricato con ${tradeCount} trade.`,
      '/data'
    );
  }

  /**
   * Helper: Create notification for report generation
   */
  static async notifyReportGenerated(userId: string, reportName: string): Promise<void> {
    await this.createNotification(
      userId,
      'success',
      'Report Generato',
      `Il report "${reportName}" è stato generato con successo.`,
      '/reports'
    );
  }

  /**
   * Helper: Create notification for feedback submission
   */
  static async notifyFeedbackSubmitted(userId: string): Promise<void> {
    await this.createNotification(
      userId,
      'success',
      'Feedback Inviato',
      'Grazie per il tuo feedback! Lo esamineremo al più presto.',
      '/settings?tab=feedback'
    );
  }

  /**
   * Helper: Create notification for comparison created
   */
  static async notifyComparisonCreated(userId: string, comparisonName: string): Promise<void> {
    await this.createNotification(
      userId,
      'success',
      'Confronto Creato',
      `Il confronto "${comparisonName}" è stato salvato.`,
      '/reports'
    );
  }

  /**
   * Helper: Create notification for diary entry
   */
  static async notifyDiaryEntryCreated(userId: string): Promise<void> {
    await this.createNotification(
      userId,
      'success',
      'Nota Diario Salvata',
      'La tua nota è stata salvata nel diario.',
      '/diary'
    );
  }

  /**
   * Helper: Create error notification
   */
  static async notifyError(userId: string, title: string, message: string): Promise<void> {
    await this.createNotification(
      userId,
      'error',
      title,
      message
    );
  }

  /**
   * Helper: Create info notification
   */
  static async notifyInfo(userId: string, title: string, message: string, actionUrl?: string): Promise<void> {
    await this.createNotification(
      userId,
      'info',
      title,
      message,
      actionUrl
    );
  }

  /**
   * Subscribe to real-time notification updates
   */
  static subscribeToNotifications(
    userId: string,
    onNotification: (notification: UserNotification) => void
  ): (() => void) {
    const subscription = supabase
      .channel('user_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          onNotification(payload.new as UserNotification);
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  }
}
